import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Mail } from 'lucide-react';
import Navbar from '@/components/Navbar';
import PageTransition from '@/components/PageTransition';
import { AnimatedButton } from '@/components/AnimatedButton';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';

interface PlanFeature {
  name: string;
  included: boolean;
}

interface PricingPlan {
  name: string;
  price: string;
  description: string;
  features: PlanFeature[];
  buttonText: string;
  popular?: boolean;
  index: number;
}

const PlanCard: React.FC<PricingPlan> = ({ 
  name, 
  price, 
  description, 
  features, 
  buttonText, 
  popular, 
  index 
}) => {
  return (
    <div 
      className={`
        bg-card border rounded-xl p-6 flex flex-col h-full
        opacity-0 animate-fade-up relative
        ${popular ? 'border-primary shadow-subtle' : 'border-border'}
      `} 
      style={{ animationDelay: `${index * 150}ms` }}
    >
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-medium">
          Most Popular
        </div>
      )}
      <div className="mb-5">
        <h3 className="text-xl font-semibold mb-2">{name}</h3>
        <p className="text-muted-foreground mb-4">{description}</p>
        <div className="flex items-baseline mb-4">
          <span className="text-3xl font-bold">{price}</span>
          {price !== 'Free' && <span className="text-muted-foreground ml-2">one-time</span>}
        </div>
      </div>
      
      <ul className="space-y-3 mb-8 flex-1">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-center">
            {feature.included ? (
              <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
            ) : (
              <X className="h-5 w-5 text-muted-foreground mr-3 flex-shrink-0" />
            )}
            <span className={!feature.included ? "text-muted-foreground" : ""}>
              {feature.name}
            </span>
          </li>
        ))}
      </ul>
      
      <div className="mt-auto">
        <Link to="/signup" className="w-full">
          {popular ? (
            <AnimatedButton className="w-full">
              {buttonText}
            </AnimatedButton>
          ) : (
            <Button variant="outline" className="w-full">
              {buttonText}
            </Button>
          )}
        </Link>
      </div>
    </div>
  );
};

const Pricing = () => {
  const plans: PricingPlan[] = [
    {
      name: "Free",
      price: "Free",
      description: "Basic emergency plan summary",
      features: [
        { name: "2-page summary report", included: true },
        { name: "Critical action items", included: true },
        { name: "Resource list", included: true },
        { name: "Email support", included: true },
        { name: "Family sharing", included: false },
        { name: "Chat with plan", included: false },
        { name: "Unlimited plan updates", included: false },
        { name: "Multiple users", included: false },
      ],
      buttonText: "Get Started",
      index: 0
    },
    {
      name: "Individual",
      price: "$29.99",
      description: "Complete emergency plan for individuals",
      features: [
        { name: "Comprehensive emergency plan", included: true },
        { name: "Detailed risk assessment", included: true },
        { name: "Complete resource guide", included: true },
        { name: "Priority email support", included: true },
        { name: "Single user access", included: true },
        { name: "Plan updates ($9.99 each)", included: true },
        { name: "Monthly premium upgrade available", included: true },
        { name: "New plans ($19.99 each)", included: true },
      ],
      buttonText: "Get Individual Plan",
      popular: true,
      index: 1
    },
    {
      name: "Business",
      price: "$49.99",
      description: "Enterprise solution for organizations",
      features: [
        { name: "Comprehensive emergency plan", included: true },
        { name: "Enterprise risk assessment", included: true },
        { name: "Company-specific resources", included: true },
        { name: "Priority support", included: true },
        { name: "Up to 3 users (same domain)", included: true },
        { name: "Monthly subscription option", included: true },
        { name: "24/7 virtual assistant access", included: true },
        { name: "Unlimited plan updates with subscription", included: true },
      ],
      buttonText: "Get Business Plan",
      index: 2
    }
  ];

  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-[200px] md:pt-[200px] pb-16">
          <div className="container px-4 md:px-6">
            <div className="text-center max-w-[800px] mx-auto mb-12 opacity-0 animate-fade-up">
              <h1 className="hero-text text-balance mb-6">
                Simple, Transparent <span className="text-primary">Pricing</span>
              </h1>
              <p className="body-lg text-muted-foreground mb-6 mx-auto max-w-[650px]">
                Choose the plan that's right for you, your family, or your business. 
                All plans include our core emergency planning features.
              </p>
            </div>
          </div>
        </section>
        
        {/* Pricing Cards */}
        <section className="py-10 pb-20">
          <div className="container px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((plan) => (
                <PlanCard key={plan.name} {...plan} />
              ))}
            </div>
            
            {/* Premium Subscription Information */}
            <div className="mt-12 bg-primary/5 border border-primary/20 rounded-xl p-6 max-w-3xl mx-auto opacity-0 animate-fade-up" style={{ animationDelay: '450ms' }}>
              <h3 className="text-xl font-semibold mb-3">Individual Premium Subscription</h3>
              <p className="mb-4 text-muted-foreground">
                Upgrade to premium for just <span className="font-bold">$9.99/month</span> after purchasing your individual plan and get:
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span>Family sharing (up to 5 users)</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span>24/7 virtual assistant for plan questions</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span>Unlimited plan updates when needed</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground">
                Premium subscription will be offered after your first plan is generated, or can be added during checkout.
              </p>
            </div>
            
            <div className="mt-8 text-center text-muted-foreground text-sm opacity-0 animate-fade-up" style={{ animationDelay: '600ms' }}>
              <p>Business users: Add monthly support ($14.99/month) for unlimited updates and virtual assistant access.</p>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-12 opacity-0 animate-fade-up">
              <h2 className="heading-xl mb-4">Frequently Asked Questions</h2>
              <p className="body-base text-muted-foreground mx-auto max-w-[600px]">
                Have questions about our plans? Find answers to common questions below.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[1000px] mx-auto">
              <div className="bg-card border border-border rounded-xl p-6 opacity-0 animate-fade-up" style={{ animationDelay: '100ms' }}>
                <h3 className="text-lg font-semibold mb-2">What's included in a plan update?</h3>
                <p className="text-muted-foreground">Plan updates refresh your emergency plan with new information, changed circumstances, or additional family members. Each update for Individual plans costs $9.99, or is included with the premium subscription.</p>
              </div>
              
              <div className="bg-card border border-border rounded-xl p-6 opacity-0 animate-fade-up" style={{ animationDelay: '200ms' }}>
                <h3 className="text-lg font-semibold mb-2">What is the "Chat with Plan" feature?</h3>
                <p className="text-muted-foreground">Chat with Plan gives you 24/7 access to a virtual assistant that can answer questions about your emergency plan, provide clarification, and help with planning work. It's available with Premium and Business subscriptions.</p>
              </div>
              
              <div className="bg-card border border-border rounded-xl p-6 opacity-0 animate-fade-up" style={{ animationDelay: '300ms' }}>
                <h3 className="text-lg font-semibold mb-2">Can I upgrade to Premium later?</h3>
                <p className="text-muted-foreground">Yes! You can start with an Individual plan and upgrade to Premium at any time. The Premium subscription will be offered after your first plan is generated, or during the checkout process.</p>
              </div>
              
              <div className="bg-card border border-border rounded-xl p-6 opacity-0 animate-fade-up" style={{ animationDelay: '400ms' }}>
                <h3 className="text-lg font-semibold mb-2">How does Business user access work?</h3>
                <p className="text-muted-foreground">Business plans allow up to 4 users from the same company email domain to access an organization's emergency planing documents. Each user will be able to view and interact with the plan using their company email address.</p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Business Contact Info */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground">
            Need help choosing the right plan for your business? <a href="mailto:support@myqoop.com" className="text-primary hover:underline flex items-center justify-center gap-1 mt-1">
              <Mail className="h-4 w-4" /> Contact our sales team
            </a>
          </p>
        </div>
        
        {/* CTA Section */}
        <section className="py-20">
          <div className="container px-4 md:px-6">
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 md:p-10 relative overflow-hidden">
              <div className="relative z-10 max-w-[600px] mx-auto text-center opacity-0 animate-fade-up">
                <h2 className="heading-lg mb-4">Ready to get prepared?</h2>
                <p className="body-base text-muted-foreground mb-8">
                  Start with a plan today and ensure your family or business is ready for whatever comes your way.
                  Our team is always available to help you choose the right option.
                </p>
                <Link to="/signup">
                  <AnimatedButton size="lg">
                    Create Your Plan Now
                  </AnimatedButton>
                </Link>
              </div>
            </div>
          </div>
        </section>
        
        {/* Footer */}
        <footer className="py-12 bg-muted/30 border-t border-border">
          <div className="container px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="col-span-1 md:col-span-2">
                <h3 className="text-lg font-bold mb-4">EmergencyPlan</h3>
                <p className="text-muted-foreground mb-4 max-w-[360px]">
                  AI-powered emergency planning for families and businesses. 
                  Stay prepared for any situation.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-4">Pages</h4>
                <ul className="space-y-2">
                  <li><Link to="/" className="text-sm text-muted-foreground hover:text-foreground">Home</Link></li>
                  <li><Link to="/features" className="text-sm text-muted-foreground hover:text-foreground">Features</Link></li>
                  <li><Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground">Pricing</Link></li>
                  <li><Link to="/about" className="text-sm text-muted-foreground hover:text-foreground">About</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-4">Legal</h4>
                <ul className="space-y-2">
                  <li><Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground">Privacy Policy</Link></li>
                  <li><Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground">Terms of Service</Link></li>
                </ul>
              </div>
            </div>
            <div className="mt-12 pt-8 border-t border-border">
              <p className="text-sm text-muted-foreground text-center">
                © {new Date().getFullYear()} EmergencyPlan. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
};

export default Pricing;
